import dayjs, { Dayjs } from "dayjs";
import { SelectOption } from "components/common/FormDialog/types";

export const BACKUP_FREQUENCIES: SelectOption[] = [
  { label: "Every day", value: "daily" },
  { label: "Every week", value: "weekly" },
  { label: "Every month", value: "monthly" },
  { label: "Every hour", value: "hourly" },
  { label: "Custom cron (advanced)", value: "custom" }
];
export const BACKUP_WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday"
].map((label, day) => ({ label, value: String(day) }));

export type BackupScheduleForm = {
  frequency: SelectOption;
  time?: Dayjs | null;
  weekday?: SelectOption;
  monthDay?: number;
  minute?: number;
  cron?: string;
  timezone: SelectOption;
  retentionMode: SelectOption;
  retentionDays?: number;
  retention?: string;
};

// Preserve schedules outside the guided forms verbatim as an advanced schedule.
export const backupScheduleDraft = (
  cron = "0 1 * * *",
  retention = "7d0h0m"
) => {
  const fields = cron.trim().split(/\s+/);
  const [minute, hour, monthDay, month, weekday] = fields;
  const integer = (value: string | undefined, min: number, max: number) =>
    Boolean(
      value &&
      /^\d+$/.test(value) &&
      Number(value) >= min &&
      Number(value) <= max
    );
  let frequency = "custom";
  if (fields.length === 5 && integer(minute, 0, 59) && month === "*") {
    if (hour === "*" && monthDay === "*" && weekday === "*")
      frequency = "hourly";
    if (integer(hour, 0, 23)) {
      if (monthDay === "*" && weekday === "*") frequency = "daily";
      else if (monthDay === "*" && integer(weekday, 0, 7)) frequency = "weekly";
      else if (integer(monthDay, 1, 31) && weekday === "*")
        frequency = "monthly";
    }
  }
  const days = /^(\d+)d0h0m$/i.exec(retention)?.[1];
  return {
    frequency: BACKUP_FREQUENCIES.find((option) => option.value === frequency)!,
    time: dayjs()
      .startOf("day")
      .hour(integer(hour, 0, 23) ? Number(hour) : 1)
      .minute(integer(minute, 0, 59) ? Number(minute) : 0),
    weekday: BACKUP_WEEKDAYS[integer(weekday, 0, 7) ? Number(weekday) % 7 : 1],
    monthDay: integer(monthDay, 1, 31) ? Number(monthDay) : 1,
    minute: integer(minute, 0, 59) ? Number(minute) : 0,
    cron,
    retentionMode: {
      label: days && Number(days) > 0 ? "Days" : "Custom duration",
      value: days && Number(days) > 0 ? "days" : "custom"
    },
    retentionDays: days && Number(days) > 0 ? Number(days) : 7,
    retention
  };
};

export const backupCron = (
  form: Pick<
    BackupScheduleForm,
    "frequency" | "time" | "weekday" | "monthDay" | "minute" | "cron"
  >
): string => {
  if (!form.frequency) return "";
  if (form.frequency.value === "custom") return form.cron?.trim() ?? "";
  if (form.frequency.value === "hourly") {
    const minute = Number(form.minute);
    return form.minute !== undefined &&
      String(form.minute) !== "" &&
      Number.isInteger(minute) &&
      minute >= 0 &&
      minute <= 59
      ? `${minute} * * * *`
      : "";
  }
  if (!dayjs.isDayjs(form.time) || !form.time.isValid()) return "";
  const clock = `${form.time.minute()} ${form.time.hour()}`;
  if (form.frequency.value === "weekly") {
    const weekday = Number(form.weekday?.value);
    return form.weekday &&
      Number.isInteger(weekday) &&
      weekday >= 0 &&
      weekday <= 6
      ? `${clock} * * ${weekday}`
      : "";
  }
  if (form.frequency.value === "monthly") {
    const day = Number(form.monthDay);
    return Number.isInteger(day) && day >= 1 && day <= 31
      ? `${clock} ${day} * *`
      : "";
  }
  return form.frequency.value === "daily" ? `${clock} * * *` : "";
};

export const describeBackupSchedule = (cron: string, timezone?: string) => {
  const draft = backupScheduleDraft(cron);
  const suffix = timezone ? ` (${timezone})` : "";
  const clock = draft.time.format("HH:mm");
  switch (draft.frequency.value) {
    case "daily":
      return `Every day at ${clock}${suffix}`;
    case "weekly":
      return `Every ${draft.weekday.label} at ${clock}${suffix}`;
    case "monthly":
      return `Day ${draft.monthDay} of each month at ${clock}${suffix}`;
    case "hourly":
      return `Every hour at minute ${draft.minute}${suffix}`;
    default:
      return `Custom schedule: ${cron}${suffix}`;
  }
};

export const describeBackupRetention = (retention?: string) => {
  const match = /^(\d+)d(\d+)h(\d+)m$/i.exec(retention ?? "");
  if (!match) return retention || "Not configured";
  return (
    [
      Number(match[1]) ? `${Number(match[1])} days` : "",
      Number(match[2]) ? `${Number(match[2])} hours` : "",
      Number(match[3]) ? `${Number(match[3])} minutes` : ""
    ]
      .filter(Boolean)
      .join(", ") || retention
  );
};
